import { useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useAuth } from '../../../hooks';

import { Card, Button, Switch, Pagination, Select, TextInput } from '@pella/sash-framework';
import { ConfirmDeleteModal } from '../../atoms';
import { ChildSetups } from './ChildSetups';

import * as classes from './SetupCards.module.css';

const PAGE_SIZES = [10, 25, 50];

/**
 * SetupCards Molecule Component
 * @param {Local.SetupCardsProps} props - Component props
 */
export const SetupCards = ({ data, config }) => {
  const {
    id,
    pagination,
    fields,
    childSetups,
    strictConfirmDelete,
    manageChildren,
    onCopy,
    onEdit,
    onDelete,
    onStatus,
  } = config;

  const { canDeleteSetups } = useAuth();
  const isMutating = useIsMutating();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [pageTransition, setPageTransition] = useState('');
  const [confirmData, setConfirmData] = useState(/** @type {Local.SetupCardRecord | undefined} */ (undefined));

  const totalItems = data?.length ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Clamp page to valid range during render - no useEffect needed!
  const validPage = pagination ? Math.min(Math.max(1, page), totalPages || 1) : 1;

  const startItemIndex = (page - 1) * itemsPerPage + 1;
  const endItemIndex = Math.min(page * itemsPerPage, totalItems);
  const paginatedData = pagination ? data?.slice((validPage - 1) * itemsPerPage, validPage * itemsPerPage) : data;

  /**
   * Handle delete action
   * @param {Local.SetupCardRecord} record - The record being deleted
   */
  const handleOnDelete = (record) => setConfirmData(record);

  /** Handle confirm delete action */
  const handleConfirmDelete = () => {
    /* v8 ignore next */
    if (onDelete && confirmData) onDelete(confirmData.id);
    setConfirmData(undefined);
  };

  /**
   * Handle page change with transition
   * @param {number} newPage - The new page number
   */
  const handlePageChange = (newPage) => {
    setPageTransition(newPage > page ? classes.nextPage : classes.prevPage);
    setPage(newPage);
  };

  return (
    paginatedData && (
      <div data-testid={id} className={classes.container}>
        <div key={page} className={`${classes.cards} ${pageTransition}`}>
          {paginatedData.map((record) => (
            <Card
              key={`setup-card-${record.description}-${record.id}`}
              data-testid={`setup-card-${record.description}`}
              className={`${classes.card} ${record.active === false ? classes.disabled : ''}`}
            >
              <div className={` ${classes.cardHeader} ${record.values ? classes.secondaryHeader : ''}`}>
                <div className={classes.description}>{record.description}</div>
                <div className={classes.buttons}>
                  {onCopy && (
                    <Button
                      data-testid={`setup-card-copy-${record.description}`}
                      className={classes.iconButton}
                      color='tertiary'
                      iconLeft='content_copy'
                      aria-label={`Copy ${record.description}`}
                      onClick={() => onCopy(record)}
                    />
                  )}
                  {onEdit && (
                    <Button
                      data-testid={`setup-card-edit-${record.description}`}
                      className={classes.iconButton}
                      color='tertiary'
                      iconLeft='edit_square'
                      onClick={() => onEdit(record)}
                    />
                  )}
                  {onDelete && (!strictConfirmDelete || canDeleteSetups) && (
                    <Button
                      data-testid={`setup-card-delete-${record.description}`}
                      className={classes.iconButton}
                      color='tertiary'
                      iconLeft='delete'
                      onClick={() => handleOnDelete(record)}
                    />
                  )}
                </div>
              </div>
              {record?.message && (
                <div className={classes.message} role='note' title={record.message} aria-label='Setup message'>
                  {record.message}
                </div>
              )}

              <div className={classes.cardContent}>
                {onStatus && (
                  <div className={classes.fieldRow}>
                    <span className={classes.fieldLabel}>Status</span>
                    <span className={classes.fieldValue}>
                      <Switch
                        data-testid={`status-switch-${record.description}`}
                        className={classes.statusSwitch}
                        disabled={isMutating > 0}
                        onChange={() => onStatus(record)}
                        checked={record.active}
                      />
                    </span>
                  </div>
                )}
                {fields.map(({ name, label }) => (
                  <div key={name} className={classes.fieldRow}>
                    <span className={classes.fieldLabel}>{label}</span>
                    <span className={classes.fieldValue}>{record[name]}</span>
                  </div>
                ))}
                {Boolean(childSetups) && <ChildSetups id={id} data={record} manageChildren={manageChildren} />}
                {record?.sequence && (
                  <div className={classes.sequenceContainer}>
                    <span className={classes.fieldLabel}>Sequence</span>
                    <span className={classes.fieldValue}>
                      <TextInput
                        className={classes.sequenceInput}
                        labelText=''
                        id={`sequence-${record.id}`}
                        value={record.sequence}
                        disabled
                      />
                    </span>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
        {pagination && totalItems > 0 && (
          <div data-testid={`${id}-pagination`} className={classes.footer}>
            <div className={classes.itemsPerPage}>
              Cards per page:
              <div className={classes.itemsPerPageSelectContainer}>
                <Select
                  className={classes.itemsPerPageSelect}
                  id='items-per-page-select'
                  labelText=''
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <Pagination
              className={classes.pagination}
              currentPage={page}
              onPageChange={handlePageChange}
              totalPages={totalPages}
            />
            <div className={classes.pageInfo}>
              Displaying {startItemIndex}-{endItemIndex} of {totalItems} items
            </div>
          </div>
        )}
        <ConfirmDeleteModal
          isOpen={Boolean(confirmData)}
          description={confirmData?.description}
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmData(undefined)}
          strictMessage={strictConfirmDelete}
        />
      </div>
    )
  );
};
